import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '@sonar/errors';
import { Period, Reference, TimeAggregate, TimeSlot } from '@sonar/models';

import { Result } from '../Reports';

import { getAggregatedOrdersPerStoreAsync } from './services';
import { AggregatedOrderInfo, ApiOrdersAggregate, OrderStatus } from './types';

interface UseOrdersQueryParams {
  storeIds: number[];
  period: Period;
  timeSlot: TimeSlot;
  isTrainingMode?: boolean;
  referenceCode?: Reference;
  timeAggregate?: TimeAggregate;
  // TODO: (#ORE-1467) Remove this prop when all users are migrated to new reports
  canRequestReports?: boolean;
}

type UseOrdersQueryResult = UseQueryResult<Result<AggregatedOrderInfo>[], ApiError>;

function useOrdersQuery({
  storeIds,
  period,
  timeSlot,
  isTrainingMode = false,
  referenceCode,
  timeAggregate,
  canRequestReports = true,
}: UseOrdersQueryParams): UseOrdersQueryResult {
  const aggregates: string[] = [ApiOrdersAggregate.Status];
  const selectedPeriod = period.getCappedPeriod();
  if (timeAggregate) {
    aggregates.push(timeAggregate);
  }
  const query = {
    aggregates,
    filters: [{ filterName: ApiOrdersAggregate.Status, value: OrderStatus.Closed }],
    period: selectedPeriod,
    storeIds,
    timeSlot,
    trainingMode: isTrainingMode,
  };

  return useQuery<Result<AggregatedOrderInfo>[], ApiError>(
    [
      'orders',
      'aggregates',
      { ...query, period: selectedPeriod.serialize(), timeSlot: timeSlot.serialize() },
    ],
    () => {
      const aggregatedOrdersPromises = [getAggregatedOrdersPerStoreAsync(query)];
      const referencePeriod = selectedPeriod.getReferencePeriod(referenceCode);
      if (referencePeriod) {
        aggregatedOrdersPromises.push(
          getAggregatedOrdersPerStoreAsync({
            ...query,
            period: referencePeriod,
          }),
        );
      }

      return Promise.all(aggregatedOrdersPromises);
    },
    { enabled: canRequestReports && storeIds.length > 0 },
  );
}

export default useOrdersQuery;
